import { css } from '@emotion/react';
import { useRouter } from 'next/router';

import { renderBlocks } from '@/components/blocks';
import { CourseCarousel } from '@/components/blocks/CourseCarousel';
import { CtaButton } from '@/components/blocks/CtaButton';
import { StandaloneTitle } from '@/components/blocks/StandaloneTitle';
import { screen } from '@/components/common/breakpoints';
import { Col, Row } from '@/components/layout/Grid';
import { Layout } from '@/components/layout/Layout';
import { Section } from '@/components/layout/Section';
import { ProgrammePageLayout } from '@/components/page/courses/PageLayout';
import { ProgrammeHero } from '@/components/page/courses/ProgrammeHero/ProgrammeHero';
import { ProgrammeSchema } from '@/components/page/courses/Schema/ProgrammeSchema';
import { Sidebar } from '@/components/page/courses/Sidebar';
import {
  navigationCollectionQuery,
  pageQuery,
  programmeNewQuery,
  programmesSettingsQuery,
  siteSettingsQuery,
} from '@/lib/queries';
import { getClient, readToken } from '@/lib/sanityUtils';
import {
  buildLayoutProp,
  getProgrammeConfig,
  queryParametersWithAudience,
} from '@/lib/utils';

import type { Audience } from '@/sanity/sanity.types';
import type {
  Locale,
  Navigation_exType,
  PageData,
  PageDataLike,
  ProgrammeB2BType,
  ProgrammeB2CType,
  ProgrammesSettings,
  SiteSettings,
} from '@/types/shared';
import type { GetServerSideProps, InferGetServerSidePropsType } from 'next';

const region: Locale = 'en-GB';

type ProgrammeDataType = ProgrammeB2CType | ProgrammeB2BType;

export const getServerSideProps = (async ({
  draftMode = false,
  params: { slug },
  query,
}) => {
  const client = getClient(draftMode ? { token: readToken } : undefined);

  const navigationCollection = await client.fetch<Navigation_exType[]>(
    navigationCollectionQuery({ locale: region }),
  );
  const queryParams = queryParametersWithAudience(
    ['courses', ...slug],
    navigationCollection,
  );

  let pageData: PageData | ProgrammeDataType;
  let pageAudience: Audience | null;

  const b2cAudience =
    typeof query?.b2cAudience === 'string'
      ? query?.b2cAudience
      : query?.b2cAudience?.join('/');

  if (queryParams.slug === 'courses') {
    pageData = await client.fetch<PageData>(
      pageQuery({
        ...queryParams,
        locale: region,
        b2cAudience,
      }),
    );

    pageAudience = pageData?.audienceRef;
  } else {
    const programmesSettings = await client.fetch<ProgrammesSettings>(
      programmesSettingsQuery(),
    );
    const { programmeType, programmeAudience } = getProgrammeConfig(
      slug,
      programmesSettings,
    );

    pageAudience = programmeAudience;
    pageData = await client.fetch<ProgrammeDataType>(
      programmeNewQuery({
        ...queryParams,
        programmeType,
        b2cAudience,
      }),
    );
  }

  if (!pageData) {
    return {
      notFound: true,
    };
  }

  const siteSettings = await client.fetch<SiteSettings>(
    siteSettingsQuery({ locale: region }),
  );

  return {
    props: {
      pageData,
      siteSettings,
      locale: region,
      draftMode,
      navigationCollection,
      audienceRef: pageAudience,
      token: draftMode ? readToken : '',
    },
  };
}) satisfies GetServerSideProps;

const CoursesPage = ({
  pageData,
  siteSettings,
  draftMode,
  navigationCollection,
  audienceRef,
}: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  const { asPath } = useRouter();

  if (pageData._type === 'page') {
    return (
      <Layout
        SEO={pageData?.SEO}
        canonicalPagePath={pageData?.canonicalPage?.slug.current}
        siteSettings={siteSettings}
        preview={draftMode}
        navigationCollection={navigationCollection}
        audienceRef={pageData?.audienceRef}
        layout={buildLayoutProp(pageData as PageDataLike)}
        dynamicYield={{
          contextPageType: 'PRODUCT',
          contextDataType: pageData?.title,
        }}
      >
        {renderBlocks(pageData?.blocks, {
          pageAudience: pageData?.audienceRef,
          isAudienceSwitcherEnabled: siteSettings?.isAudienceSwitcherEnabled,
        })}
      </Layout>
    );
  }

  return (
    <Layout
      SEO={pageData?.SEO}
      siteSettings={siteSettings}
      preview={draftMode}
      navigationCollection={navigationCollection}
      audienceRef={audienceRef}
      layout={{ spacingMode: 'margin' }}
      isStickyNavigation={false}
      dynamicYield={{
        contextPageType: 'PRODUCT',
        contextDataType: pageData?.name,
      }}
    >
      <ProgrammeSchema
        url={asPath}
        datePublished={pageData.publishDate}
        programmeType={pageData._type}
        programmeName={pageData.name}
        headline={pageData?.hero?.heading}
        description={pageData?.hero?.subheading}
        wistiaPopover={pageData?.video}
        aboutBadges={pageData?.skills?.badges}
        learningOverviewList={pageData?.courseSummary?.list}
        syllabusSections={pageData?.curriculumAccordion?.outerAccordion}
        qualificationLevel={pageData?.programmeCard?.qualification?.level}
        price={pageData?.sidebar?.footer?.price}
        courseSchedule={pageData?.structuredData?.courseSchedule}
      />

      <Section className="course-page" spacing={false}>
        <Row css={gridRow}>
          <Col
            sizes={{ base: 4, md: 12 }}
            start={{ base: 1, md: 1 }}
            css={heroCol}
          >
            {pageData?.hero && (
              <ProgrammeHero
                caption={pageData?.hero?.caption}
                decorative={pageData?.hero?.decorative}
                heading={pageData?.hero?.heading}
                subheading={pageData?.hero?.subheading}
              />
            )}
          </Col>

          <Col sizes={{ sm: 10, md: 6 }} start={{ sm: 2, md: 1 }}>
            <ProgrammePageLayout
              programmeData={pageData}
              isAudienceSwitcherEnabled={
                siteSettings?.isAudienceSwitcherEnabled
              }
              pageAudience={audienceRef}
            />
          </Col>

          <Col
            sizes={{ base: 4, sm: 10, md: 6, lg: 5 }}
            start={{ sm: 2, md: 7, lg: 8 }}
            css={sidebarCol}
          >
            <Sidebar
              tabs={pageData?.sidebar?.tabs}
              footer={pageData?.sidebar?.footer as any}
              pageAudience={audienceRef}
              isAudienceSwitcherEnabled={
                siteSettings?.isAudienceSwitcherEnabled
              }
            />
          </Col>
        </Row>
      </Section>

      {pageData?.relatedSection?.programmes?.length > 0 && (
        <>
          {pageData?.relatedSection?.title?.heading.length > 0 && (
            <StandaloneTitle
              headingLevel={2}
              heading={pageData?.relatedSection?.title?.heading}
              subheading={pageData?.relatedSection?.title?.subheading}
              smallSubheading
              blockSpacing="xl"
              size="sm"
            />
          )}

          <CourseCarousel
            programmes={pageData?.relatedSection.programmes as any}
            pageAudience={audienceRef}
            isAudienceSwitcherEnabled={siteSettings?.isAudienceSwitcherEnabled}
            blockSpacing="sm"
          />

          {pageData?.relatedSection?.CTA?.url && (
            <CtaButton
              alignment="left"
              blockSpacing="xs"
              isAudienceSwitcherEnabled={
                siteSettings?.isAudienceSwitcherEnabled
              }
              pageAudience={audienceRef}
              variant="solid-light"
              CTA={pageData?.relatedSection?.CTA as any}
            />
          )}
        </>
      )}
    </Layout>
  );
};

export default CoursesPage;

const gridRow = css`
  row-gap: 0 !important;
  padding-bottom: 24px;
`;

const heroCol = css`
  grid-row: 1;
`;

const sidebarCol = css`
  grid-row: 2;

  ${screen.md} {
    grid-row: 1 / span 2;
  }
`;
